<template>
  <header class="bg-primary-800 shadow-md sticky top-0 z-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">

        <!-- Logo -->
        <router-link to="/" class="flex items-center gap-3 flex-shrink-0">
          <img src="@/assets/jengahills.jpeg" alt="Jenga Hills" class="h-10 w-auto rounded" />
          <span class="hidden sm:block text-white font-bold text-lg tracking-tight leading-tight">
            Jenga Hills
          </span>
        </router-link>

        <!-- Desktop nav -->
        <nav class="hidden md:flex items-center gap-1">

          <!-- Home -->
          <router-link
            to="/"
            class="nav-link"
            exact-active-class="nav-link-active"
          >Home</router-link>

          <!-- Shop dropdown -->
          <div class="relative" @mouseenter="shopOpen = true" @mouseleave="shopOpen = false">
            <button
              class="nav-link flex items-center gap-1"
              :class="{ 'nav-link-active': isShopRoute }"
            >
              Shop
              <svg class="w-3.5 h-3.5 transition-transform" :class="{ 'rotate-180': shopOpen }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <!-- Mega dropdown -->
            <div
              v-show="shopOpen"
              class="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[520px] bg-white rounded-2xl shadow-xl border border-secondary-200 p-5 z-50"
            >
              <div class="grid grid-cols-2 gap-1 mb-4">
                <router-link
                  v-for="cat in categories"
                  :key="cat.id"
                  :to="`/shop?category=${cat.slug}`"
                  class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-secondary-100 transition-colors group"
                  @click="shopOpen = false"
                >
                  <div class="w-8 h-8 bg-primary-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-accent-300 transition-colors">
                    <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <span class="text-sm font-medium text-primary-800">{{ cat.name }}</span>
                </router-link>
              </div>
              <div class="border-t border-secondary-200 pt-3">
                <router-link
                  to="/shop"
                  class="flex items-center justify-center gap-2 w-full bg-primary-800 hover:bg-primary-700 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors"
                  @click="shopOpen = false"
                >
                  View All Products
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Vendors -->
          <router-link to="/vendors" class="nav-link" active-class="nav-link-active">
            Vendors
          </router-link>

          <!-- About -->
          <router-link to="/about" class="nav-link" active-class="nav-link-active">
            About
          </router-link>

          <!-- Contact -->
          <router-link to="/contact" class="nav-link" active-class="nav-link-active">
            Contact
          </router-link>

          <!-- Vendor portal -->
          <router-link
            v-if="isVendor && !isAdmin"
            to="/vendor"
            class="nav-link"
            active-class="nav-link-active"
          >My Store</router-link>

          <!-- Admin -->
          <router-link
            v-if="isAdmin"
            to="/admin"
            class="nav-link"
            active-class="nav-link-active"
          >Admin</router-link>
        </nav>

        <!-- Right actions -->
        <div class="flex items-center gap-1">

          <!-- Search -->
          <button
            class="p-2 text-secondary-200 hover:text-white transition-colors rounded-lg"
            aria-label="Search"
            @click="$router.push('/shop')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
          </button>

          <!-- Cart -->
          <button
            class="relative p-2 text-secondary-200 hover:text-white transition-colors rounded-lg"
            aria-label="Cart"
            @click="store.dispatch('cart/toggleCart')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.5 6h13M7 13L5.4 5M10 21a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm9 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
            </svg>
            <span
              v-if="cartCount > 0"
              class="absolute -top-0.5 -right-0.5 bg-accent-300 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center leading-none"
            >{{ cartCount > 9 ? '9+' : cartCount }}</span>
          </button>

          <!-- Auth -->
          <template v-if="isAuthenticated">
            <div class="relative">
              <button
                class="flex items-center gap-2 text-secondary-200 hover:text-white transition-colors text-sm font-medium px-2 py-1 rounded-lg"
                @click="userMenuOpen = !userMenuOpen"
              >
                <div class="w-7 h-7 bg-accent-300 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {{ userInitial }}
                </div>
                <svg class="w-3.5 h-3.5 transition-transform" :class="{ 'rotate-180': userMenuOpen }"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                v-if="userMenuOpen"
                class="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1.5 z-50 border border-secondary-200"
              >
                <div class="px-4 py-2 border-b border-secondary-100">
                  <p class="text-xs font-semibold text-primary-800">{{ currentUser?.name }}</p>
                  <p class="text-xs text-secondary-400 truncate">{{ currentUser?.email }}</p>
                </div>
                <router-link to="/profile" class="block px-4 py-2 text-sm text-primary-800 hover:bg-secondary-100"
                  @click="userMenuOpen = false">My Profile</router-link>
                <router-link to="/orders" class="block px-4 py-2 text-sm text-primary-800 hover:bg-secondary-100"
                  @click="userMenuOpen = false">My Orders</router-link>
                <router-link to="/account/settings" class="block px-4 py-2 text-sm text-primary-800 hover:bg-secondary-100"
                  @click="userMenuOpen = false">Settings</router-link>
                <router-link v-if="isVendor && !isAdmin" to="/vendor" class="block px-4 py-2 text-sm text-primary-800 hover:bg-secondary-100"
                  @click="userMenuOpen = false">Vendor Dashboard</router-link>
                <router-link v-if="isAdmin" to="/admin" class="block px-4 py-2 text-sm text-primary-800 hover:bg-secondary-100"
                  @click="userMenuOpen = false">Admin Panel</router-link>
                <hr class="my-1 border-secondary-100" />
                <button class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  @click="logout">Sign Out</button>
              </div>
            </div>
          </template>
          <template v-else>
            <router-link to="/login"
              class="text-sm font-medium text-secondary-200 hover:text-white transition-colors px-3 py-1.5 rounded-lg">
              Sign In
            </router-link>
            <router-link to="/register"
              class="text-sm font-medium bg-accent-300 hover:bg-accent-400 text-white px-4 py-1.5 rounded-lg transition-colors">
              Register
            </router-link>
          </template>

          <!-- Mobile menu toggle -->
          <button
            class="md:hidden p-2 text-secondary-200 hover:text-white transition-colors rounded-lg ml-1"
            @click="mobileMenuOpen = !mobileMenuOpen"
            aria-label="Menu"
          >
            <svg v-if="!mobileMenuOpen" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <transition name="slide-down">
      <div v-if="mobileMenuOpen" class="md:hidden bg-primary-900 border-t border-primary-700">
        <div class="px-4 py-3 space-y-1">
          <router-link to="/" class="mobile-link" @click="mobileMenuOpen = false">Home</router-link>
          <router-link to="/shop" class="mobile-link" @click="mobileMenuOpen = false">Shop</router-link>

          <!-- Mobile categories -->
          <div class="pl-3 border-l-2 border-primary-700 ml-2 space-y-1">
            <router-link
              v-for="cat in categories.slice(0, 4)"
              :key="cat.id"
              :to="`/shop?category=${cat.slug}`"
              class="block py-1.5 text-xs text-secondary-400 hover:text-white transition-colors"
              @click="mobileMenuOpen = false"
            >{{ cat.name }}</router-link>
          </div>

          <router-link to="/vendors" class="mobile-link" @click="mobileMenuOpen = false">Vendors</router-link>
          <router-link to="/about" class="mobile-link" @click="mobileMenuOpen = false">About</router-link>
          <router-link to="/contact" class="mobile-link" @click="mobileMenuOpen = false">Contact</router-link>

          <div class="border-t border-primary-700 pt-2 mt-2 space-y-1">
            <router-link v-if="isAuthenticated" to="/profile" class="mobile-link" @click="mobileMenuOpen = false">My Profile</router-link>
            <router-link v-if="isAuthenticated" to="/orders" class="mobile-link" @click="mobileMenuOpen = false">My Orders</router-link>
            <router-link v-if="isAuthenticated" to="/account/settings" class="mobile-link" @click="mobileMenuOpen = false">Settings</router-link>
            <router-link v-if="isVendor && !isAdmin" to="/vendor" class="mobile-link" @click="mobileMenuOpen = false">My Store</router-link>
            <router-link v-if="isAdmin" to="/admin" class="mobile-link" @click="mobileMenuOpen = false">Admin</router-link>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

export default defineComponent({
  name: 'AppNavbar',
  setup () {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const mobileMenuOpen = ref(false)
    const userMenuOpen = ref(false)
    const shopOpen = ref(false)

    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])
    const isAdmin = computed(() => store.getters['auth/isAdmin'])
    const isVendor = computed(() => store.getters['auth/isVendor'])
    const cartCount = computed(() => store.getters['cart/itemCount'])
    const currentUser = computed(() => store.getters['auth/currentUser'])
    const userInitial = computed(() => currentUser.value?.name?.charAt(0).toUpperCase() ?? 'U')
    const categories = computed(() => store.state.products.categories)
    const isShopRoute = computed(() => route.path.startsWith('/shop'))

    function toggleCart () {
      router.push('/cart')
    }

    async function logout () {
      userMenuOpen.value = false
      await store.dispatch('auth/logout')
      router.push('/login')
    }

    return {
      store,
      mobileMenuOpen,
      userMenuOpen,
      shopOpen,
      isAuthenticated,
      isAdmin,
      isVendor,
      cartCount,
      currentUser,
      userInitial,
      categories,
      isShopRoute,
      toggleCart,
      logout
    }
  }
})
</script>

<style scoped>
.nav-link {
  @apply text-secondary-200 hover:text-white text-sm font-medium transition-colors px-3 py-2 rounded-lg hover:bg-primary-700;
}
.nav-link-active {
  @apply text-white bg-primary-700;
}
.mobile-link {
  @apply block text-secondary-200 hover:text-white py-2 px-2 text-sm font-medium transition-colors rounded-lg hover:bg-primary-700;
}
.slide-down-enter-active, .slide-down-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.slide-down-enter-from, .slide-down-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
